import { ApolloClient, gql, QueryHookOptions } from "@apollo/client"
import { useMemo } from "react"
import { useQuery } from "@oysterhr/core"
import { Query, Subdivision } from "@oysterhr/schema"

export const GET_SUBDIVISIONS = gql`
  query CountrySubdivisionsQuery($isoAlpha2Code: String!) {
    country(isoAlpha2Code: $isoAlpha2Code) {
      isoLongName
      isoShortName
      alpha2
      subdivisions {
        name
        code
        type
      }
    }
  }
`

/** Subdivisions array exists and is empty */
const getSubdivisionsArrayEmpty = (subdivisions?: Subdivision[]) =>
  subdivisions && Array.isArray(subdivisions) && subdivisions.length === 0

/**
 * async function to be used in validation schema.
 */
export const getSubdivisions = async (client: ApolloClient<unknown>, countryCode: string) => {
  // Skip querying the BE with missing countryCode
  if (!countryCode) return null

  const queryReturn = await client.query<GetSubdivisionsQueryData>({
    query: GET_SUBDIVISIONS,
    variables: { isoAlpha2Code: countryCode },
  })

  const isSubdivisionsArrayEmpty = getSubdivisionsArrayEmpty(
    queryReturn?.data?.country?.subdivisions,
  )
  return { ...queryReturn, isSubdivisionsArrayEmpty }
}

export type GetSubdivisionsQueryData = { country: Query["country"] }

export const useSubdivisions = (
  countryCode?: string,
  options: QueryHookOptions<GetSubdivisionsQueryData> = {},
) => {
  const useQueryReturn = useQuery<GetSubdivisionsQueryData>(GET_SUBDIVISIONS, {
    ...options,
    variables: { isoAlpha2Code: countryCode },
    skip: !countryCode,
  })

  const isSubdivisionsArrayEmpty = useMemo(() => {
    return getSubdivisionsArrayEmpty(useQueryReturn?.data?.country?.subdivisions)
  }, [useQueryReturn?.data?.country?.subdivisions])

  return { ...useQueryReturn, isSubdivisionsArrayEmpty }
}
